/**
 * AddNewCompanyModal — Iter-297 — Admin instant company creation.
 *
 * Renders from the gig-creation flow when an admin needs to post a
 * shift for a brand that hasn't signed up yet. Form fields match the
 * spec in CHANGELOG (company_name + contact info + billing email +
 * notes). On submit, calls POST /api/admin/clients/stub. The newly
 * created stub client is handed back to the caller's onCreated so it
 * can be set as the selected client.
 *
 * Stays SILENT — no email/invite is dispatched server-side. The admin
 * triggers that separately through the existing client-invite flow.
 */
import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Button } from './ui/button';
import { Loader2, Building2, Mail, Phone, Receipt, StickyNote, User } from 'lucide-react';
import api from '../utils/api';
import { toast } from 'sonner';

const initialForm = {
  company_name: '',
  contact_name: '',
  contact_email: '',
  contact_phone: '',
  billing_email: '',
  notes: '',
};

export default function AddNewCompanyModal({ open, onClose, onCreated }) {
  const [form, setForm] = useState(initialForm);
  const [submitting, setSubmitting] = useState(false);

  const update = (k, v) => setForm((prev) => ({ ...prev, [k]: v }));

  const reset = () => setForm(initialForm);

  const handleClose = () => {
    if (submitting) return;
    reset();
    onClose?.();
  };

  const handleSubmit = async (e) => {
    e?.preventDefault?.();
    const name = (form.company_name || '').trim();
    if (!name) {
      toast.error('Company name is required.');
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        company_name: name,
        contact_name: form.contact_name.trim() || undefined,
        contact_email: form.contact_email.trim() || undefined,
        contact_phone: form.contact_phone.trim() || undefined,
        billing_email: form.billing_email.trim() || undefined,
        notes: form.notes.trim() || undefined,
      };
      const res = await api.post('/admin/clients/stub', payload);
      toast.success(`${name} added — no invite sent yet.`);
      onCreated?.(res.data);
      reset();
      onClose?.();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Failed to add company');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && handleClose()}>
      <DialogContent className="sm:max-w-lg" data-testid="add-company-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Building2 className="w-5 h-5 text-cyan-600" />
            Add New Company
          </DialogTitle>
          <DialogDescription className="text-xs leading-relaxed">
            Create a client record for a company that hasn&apos;t registered yet.
            No emails or invitations are sent. You can invite them later from
            the client list.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-3">
          <div className="space-y-1.5">
            <Label htmlFor="company_name" className="flex items-center gap-1.5 text-xs font-semibold">
              <Building2 className="w-3.5 h-3.5" />
              Company Name <span className="text-rose-500">*</span>
            </Label>
            <Input
              id="company_name"
              value={form.company_name}
              onChange={(e) => update('company_name', e.target.value)}
              placeholder="e.g. Acme Sparkling Water Co"
              autoFocus
              required
              maxLength={200}
              data-testid="company-name-input"
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="contact_name" className="flex items-center gap-1.5 text-xs font-semibold">
                <User className="w-3.5 h-3.5" />
                Contact Name
              </Label>
              <Input
                id="contact_name"
                value={form.contact_name}
                onChange={(e) => update('contact_name', e.target.value)}
                placeholder="Primary contact"
                maxLength={200}
                data-testid="contact-name-input"
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="contact_phone" className="flex items-center gap-1.5 text-xs font-semibold">
                <Phone className="w-3.5 h-3.5" />
                Contact Phone
              </Label>
              <Input
                id="contact_phone"
                type="tel"
                value={form.contact_phone}
                onChange={(e) => update('contact_phone', e.target.value)}
                placeholder="+1 555 123 4567"
                maxLength={32}
                data-testid="contact-phone-input"
              />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="contact_email" className="flex items-center gap-1.5 text-xs font-semibold">
              <Mail className="w-3.5 h-3.5" />
              Contact Email
            </Label>
            <Input
              id="contact_email"
              type="email"
              value={form.contact_email}
              onChange={(e) => update('contact_email', e.target.value)}
              placeholder="contact@company.com"
              maxLength={200}
              data-testid="contact-email-input"
            />
            <p className="text-[11px] text-gray-500">
              Leave blank to skip — you can add it before sending the invite.
            </p>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="billing_email" className="flex items-center gap-1.5 text-xs font-semibold">
              <Receipt className="w-3.5 h-3.5" />
              Billing Email
            </Label>
            <Input
              id="billing_email"
              type="email"
              value={form.billing_email}
              onChange={(e) => update('billing_email', e.target.value)}
              placeholder="ap@company.com"
              maxLength={200}
              data-testid="billing-email-input"
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="notes" className="flex items-center gap-1.5 text-xs font-semibold">
              <StickyNote className="w-3.5 h-3.5" />
              Notes
            </Label>
            <Textarea
              id="notes"
              value={form.notes}
              onChange={(e) => update('notes', e.target.value)}
              placeholder="Lead source, account manager, special terms…"
              rows={3}
              maxLength={1000}
              data-testid="notes-input"
            />
          </div>

          <DialogFooter className="gap-2 pt-2">
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              disabled={submitting}
              data-testid="cancel-add-company"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={submitting || !form.company_name.trim()}
              className="bg-cyan-600 hover:bg-cyan-700 text-white"
              data-testid="submit-add-company"
            >
              {submitting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
                  Adding…
                </>
              ) : (
                'Add Company'
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
